import { useState } from "react";
import { Plus, RefreshCw, Trash2, Users } from "lucide-react";
import { PageShell } from "./page-shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { EmptyState, ErrorState } from "@/components/common/empty-state";
import { FieldRow } from "./courses";
import { VideoUploadField } from "@/components/common/video-upload-field";
import {
  useCourses,
  useDeleteGroup,
  useGroups,
  useSaveGroup,
  useSyncGroups,
} from "@/lib/queries";
import { useToast } from "@/components/ui/toast";
import type { Group } from "@/lib/types";

export function GroupsPage() {
  const { data, isLoading, isError, refetch } = useGroups();
  const sync = useSyncGroups();
  const toast = useToast();
  const [editing, setEditing] = useState<Group | "new" | null>(null);

  const onSync = async () => {
    try {
      const result = await sync.mutateAsync();
      toast.success("Live groups synced", result?.synced ? `${result.synced} updated` : undefined);
    } catch (err) {
      toast.error("Sync failed", err instanceof Error ? err.message : undefined);
    }
  };

  return (
    <PageShell
      title="Groups"
      description="Sales-ready cohorts. AlfaCRM-linked groups stay in sync via the sync button."
      actions={
        <div className="flex items-center gap-2">
          <Button variant="outline" size="md" onClick={onSync} disabled={sync.isPending}>
            <RefreshCw className={`h-4 w-4 ${sync.isPending ? "animate-spin" : ""}`} />
            Sync live
          </Button>
          <Button variant="primary" size="md" onClick={() => setEditing("new")}>
            <Plus className="h-4 w-4" />
            New group
          </Button>
        </div>
      }
    >
      <div className="rounded-lg border border-line bg-surface">
        {isLoading ? (
          <div className="space-y-2 p-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-9 w-full" />
            ))}
          </div>
        ) : isError ? (
          <ErrorState
            title="Couldn't load groups"
            description="The API didn't respond."
            onRetry={() => void refetch()}
          />
        ) : !Array.isArray(data) || data.length === 0 ? (
          <EmptyState
            icon={Users}
            title="No groups yet"
            description="Add your first group or sync from AlfaCRM to populate this list."
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Teacher</TableHead>
                <TableHead>Schedule</TableHead>
                <TableHead>Mode / Track</TableHead>
                <TableHead>CRM</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((group) => (
                <TableRow key={group.id}>
                  <TableCell>
                    <div className="font-medium text-ink">{group.name}</div>
                    {group.level && <div className="text-xs text-ink-faint">{group.level}</div>}
                  </TableCell>
                  <TableCell>{group.teacher_name || "—"}</TableCell>
                  <TableCell className="text-sm text-ink-muted">{group.schedule_text || "—"}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      {group.learning_mode && <Badge tone="outline">{group.learning_mode}</Badge>}
                      {group.track && <Badge tone="neutral">{group.track}</Badge>}
                    </div>
                  </TableCell>
                  <TableCell>
                    {group.alfacrm_group_id ? (
                      <span className="font-mono text-sm text-ink-muted">
                        #{group.alfacrm_group_id}
                      </span>
                    ) : (
                      <Badge tone="warning">no CRM</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => setEditing(group)}>
                      Edit
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {editing !== null && (
        <GroupDialog initial={editing === "new" ? null : editing} onClose={() => setEditing(null)} />
      )}
    </PageShell>
  );
}

function GroupDialog({ initial, onClose }: { initial: Group | null; onClose: () => void }) {
  const save = useSaveGroup();
  const del = useDeleteGroup();
  const { data: courses } = useCourses();
  const toast = useToast();
  const [form, setForm] = useState<Partial<Group>>(
    initial
      ? { ...initial, subjects: initial.subjects ?? [], rules: initial.rules ?? {} }
      : {
          name: "",
          course_id: null,
          teacher_name: "",
          schedule_text: "",
          learning_mode: "",
          subjects: [],
          track: "sat",
          level: "",
          alfacrm_group_id: null,
          seat_capacity: 20,
          priority: 50,
          intro_video: "",
          teacher_info: "",
          rules: {},
        }
  );

  const [rulesText, setRulesText] = useState(
    initial?.rules ? JSON.stringify(initial.rules, null, 2) : ""
  );

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    let rules: Record<string, unknown> | null = {};
    if (rulesText.trim()) {
      try {
        rules = JSON.parse(rulesText);
      } catch {
        toast.error("Invalid rules JSON", "Double-check brackets and quoting.");
        return;
      }
    }
    try {
      await save.mutateAsync({
        id: initial?.id,
        body: {
          ...form,
          subjects: typeof form.subjects === "string"
            ? (form.subjects as unknown as string).split(",").map((s) => s.trim()).filter(Boolean)
            : (form.subjects ?? []),
          alfacrm_group_id: form.alfacrm_group_id ? Number(form.alfacrm_group_id) : null,
          seat_capacity: form.seat_capacity ? Number(form.seat_capacity) : null,
          priority: form.priority ? Number(form.priority) : null,
          rules,
        },
      });
      toast.success(initial ? "Group updated" : "Group created");
      onClose();
    } catch (err) {
      toast.error("Save failed", err instanceof Error ? err.message : undefined);
    }
  };

  const onDelete = async () => {
    if (!initial) return;
    if (!confirm("Delete this group?")) return;
    try {
      await del.mutateAsync(initial.id);
      toast.success("Group deleted");
      onClose();
    } catch (err) {
      toast.error("Delete failed", err instanceof Error ? err.message : undefined);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{initial ? "Edit group" : "New group"}</DialogTitle>
          <DialogDescription>Cohort details and AlfaCRM mapping.</DialogDescription>
        </DialogHeader>

        <form id="group-form" onSubmit={onSubmit} className="space-y-6 max-h-[60vh] overflow-y-auto scrollbar-thin pr-1">
          <FormSection title="Identity">
            <div className="grid gap-3 sm:grid-cols-2">
              <FieldRow label="Name" id="name">
                <Input
                  id="name"
                  value={form.name ?? ""}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                />
              </FieldRow>
              <FieldRow label="Course" id="course_id">
                <select
                  id="course_id"
                  value={form.course_id ?? ""}
                  onChange={(e) => setForm({ ...form, course_id: e.target.value || null })}
                  className="flex h-9 w-full rounded-md border border-line-strong bg-surface px-3 text-base text-ink"
                >
                  <option value="">No course</option>
                  {courses?.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </FieldRow>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <FieldRow label="Teacher" id="teacher_name">
                <Input
                  id="teacher_name"
                  value={form.teacher_name ?? ""}
                  onChange={(e) => setForm({ ...form, teacher_name: e.target.value })}
                  required
                />
              </FieldRow>
              <FieldRow label="Schedule" id="schedule_text" hint="Human-readable schedule">
                <Input
                  id="schedule_text"
                  value={form.schedule_text ?? ""}
                  onChange={(e) => setForm({ ...form, schedule_text: e.target.value })}
                />
              </FieldRow>
            </div>
            <FieldRow label="Teacher info" id="teacher_info">
              <Textarea
                id="teacher_info"
                rows={3}
                value={form.teacher_info ?? ""}
                onChange={(e) => setForm({ ...form, teacher_info: e.target.value })}
              />
            </FieldRow>
          </FormSection>

          <FormSection title="Sales attributes">
            <div className="grid gap-3 sm:grid-cols-3">
              <FieldRow label="Mode" id="learning_mode">
                <Input
                  id="learning_mode"
                  value={form.learning_mode ?? ""}
                  onChange={(e) => setForm({ ...form, learning_mode: e.target.value })}
                  placeholder="offline / online / hybrid"
                />
              </FieldRow>
              <FieldRow label="Track" id="track">
                <Input
                  id="track"
                  value={form.track ?? ""}
                  onChange={(e) => setForm({ ...form, track: e.target.value })}
                  placeholder="sat / presat"
                />
              </FieldRow>
              <FieldRow label="Level" id="level">
                <Input
                  id="level"
                  value={form.level ?? ""}
                  onChange={(e) => setForm({ ...form, level: e.target.value })}
                />
              </FieldRow>
            </div>
            <FieldRow label="Subjects" id="subjects" hint="Comma-separated">
              <Input
                id="subjects"
                value={Array.isArray(form.subjects) ? form.subjects.join(", ") : (form.subjects ?? "")}
                onChange={(e) => setForm({ ...form, subjects: e.target.value as unknown as string[] })}
              />
            </FieldRow>
            <div className="grid gap-3 sm:grid-cols-3">
              <FieldRow label="Seat capacity" id="seat_capacity">
                <Input
                  id="seat_capacity"
                  type="number"
                  value={(form.seat_capacity as number | undefined) ?? ""}
                  onChange={(e) => setForm({ ...form, seat_capacity: e.target.value as unknown as number })}
                />
              </FieldRow>
              <FieldRow label="Priority" id="priority">
                <Input
                  id="priority"
                  type="number"
                  value={(form.priority as number | undefined) ?? ""}
                  onChange={(e) => setForm({ ...form, priority: e.target.value as unknown as number })}
                />
              </FieldRow>
              <FieldRow label="AlfaCRM ID" id="alfacrm_group_id">
                <Input
                  id="alfacrm_group_id"
                  type="number"
                  value={(form.alfacrm_group_id as number | undefined) ?? ""}
                  onChange={(e) => setForm({ ...form, alfacrm_group_id: e.target.value as unknown as number })}
                />
              </FieldRow>
            </div>
            <FieldRow label="Intro video URL" id="intro_video" hint="Upload a demo or paste a URL.">
              <VideoUploadField
                id="intro_video"
                endpoint="/admin-upload/group-video"
                value={form.intro_video ?? ""}
                onChange={(v) => setForm({ ...form, intro_video: v })}
              />
            </FieldRow>
          </FormSection>

          <FormSection title="Matching rules" hint="JSON describing track requirements for placement.">
            <Textarea
              rows={6}
              value={rulesText}
              onChange={(e) => setRulesText(e.target.value)}
              className="font-mono text-sm"
              placeholder='{"minMathTrack": "sat", "minEnglishTrack": "sat"}'
            />
          </FormSection>
        </form>

        <DialogFooter>
          {initial && (
            <Button
              type="button"
              variant="destructive"
              onClick={onDelete}
              disabled={del.isPending}
              className="mr-auto"
            >
              <Trash2 className="h-4 w-4" /> Delete
            </Button>
          )}
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="group-form" variant="primary" disabled={save.isPending}>
            Save group
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FormSection({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3">
      <div>
        <h4 className="text-xs font-medium uppercase tracking-wider text-ink-faint">{title}</h4>
        {hint && <p className="text-xs text-ink-faint">{hint}</p>}
      </div>
      {children}
    </section>
  );
}
